import { useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { logger } from "./helpers/logHandler";
import { ArrowDownCircle, ArrowLeft } from "lucide-react";
import ZoneStorageSorter1UnloadSingles from "./zone-storage-sorter-1-unloader-singles";
import { playSuccess, playError } from "./helpers/audioPlayer";
import { logoutProcessor } from "./helpers/logoutProcessor";
import { toast } from "react-toastify";
import { useTranslation } from "../i18n";
import HiddenScanInput from "./HiddenScanInput";
import ScanStatusIcons from "./ScanStatusIcons";
import ScanFlashOverlay from "./ScanFlashOverlay";
import FitWord from "./FitWord";
import { apiFetch } from "../api/apiFetch";
import "../styles/scansheet.css";


const ZoneStorageSorter1Unload = ({ warehouse, resetFunc }: { warehouse: string; resetFunc: () => void }) => {
    const navigator = useNavigate();
    const { t } = useTranslation();

    const [scan, set_scan] = useState<string>("");
    const [lastScanned, set_lastScanned] = useState<string>("");
    const [workingLocation, set_workingLocation] = useState<string>("");
    const [workingLane, set_workingLane] = useState<string>("");
    const [workingLaneBarcode, set_workingLaneBarcode] = useState<String>("");
    const [scanStatus, set_scanStatus] = useState<"idle" | "success" | "error">("idle");
    const statusTimerRef = useRef<NodeJS.Timeout | null>(null);
    const [singlesScan, set_singlesScan] = useState<string>("");
    const singlesSubmitRef = useRef<((scannedValue?: string) => void) | null>(null);

    const registerSinglesSubmit = useCallback((fn: (scannedValue?: string) => void) => {
        singlesSubmitRef.current = fn;
    }, []);

    const handleSinglesSubmit = useCallback((scannedValue?: string) => {
        if (singlesSubmitRef.current) {
            singlesSubmitRef.current(scannedValue);
        }
    }, []);

    const laneBarcodePrefix = warehouse === "Newport" ? "NPL" : "PLL";

    const flashScanStatus = (status: "success" | "error") => {
        if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
        set_scanStatus(status);
        statusTimerRef.current = setTimeout(() => set_scanStatus("idle"), 500);
    };

    const closeUnload = () => {
        set_workingLane("");
        set_workingLaneBarcode("");
        set_workingLocation("");

        flashScanStatus("success");
        playSuccess();
    }

    const doSubmit = useCallback(async (scannedValue?: string) => {
        const val = scannedValue ?? scan;
        const scannedItem = val;
        // Reset scan input
        set_scan("");

        if (scannedItem === "") {
            playError();
            toast.dismiss(); toast.error(t("invalid_scan"));
            return;
        }


        if (scannedItem.length < 2 || scannedItem.length > 8) {
            playError();
            toast.dismiss(); toast.error(t("invalid_scan"));
            logger(warehouse, "S1 Unloader", "Many", 0, scannedItem, "Bay Location", scannedItem, "York", "error");
            return;
        }

        if (workingLocation === "") {

            // Get lane from location
            await apiFetch(`/zonestorage/scan/sorter-one-unload-lane`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({ warehouse: warehouse, location: scannedItem.slice(-2) })
            }).then(async (res): Promise<any> => {

                try {
                    let logStatus = "";

                    let response = await res.json();

                    console.log(response);

                    if (response === null) {
                        flashScanStatus("error");
                        playError();
                        toast.dismiss(); toast.error(t("server_error"));
                        logStatus = "error";

                    } else if (res.ok) {
                        flashScanStatus("success");
                        playSuccess();
                        logStatus = "success";
                        set_workingLane("L" + String(response.Lane));
                        set_workingLaneBarcode(laneBarcodePrefix + String(response.Lane).padStart(2, '0'));

                    } else if (res.status === 401) {
                        console.error("INVALID PERMISSIONS");
                        logoutProcessor();
                        navigator("/login");
                    } else {
                        console.error(response.error);
                        logStatus = "error";
                    }

                    // Create log
                    if (lastScanned === scannedItem) {
                        logStatus = "repeat"
                    }

                    if ((response !== null && response.Lane !== 3) || (response === null)) {
                        logger(warehouse, "S1 Unloader", "Many", 0, scannedItem, "Bay Location", scannedItem, "York", logStatus);
                    }

                    set_lastScanned(scannedItem);

                    if (res.ok && response !== null) {
                        // Set working location
                        set_workingLocation(scannedItem.slice(-2));
                    }

                } catch (error) {
                    let message = "Unknown Error";
                    if (error instanceof Error) message = error.message;

                    console.log(message);

                    flashScanStatus("error");
                    playError();
                    toast.dismiss(); toast.error(t("server_error"));
                    return;
                }

            })

            return;
        }

        //// If location has already been set
        // Check to see if scan is of the location
        if (val.slice(-2) === workingLocation) {
            // All good
            flashScanStatus("success");
            playSuccess();

            if (lastScanned === scannedItem) {
                logger(warehouse, "S1 Unloader", "Many", 0, scannedItem, "Bay Location", scannedItem, "York", "repeat");
            } else {
                logger(warehouse, "S1 Unloader", "Many", 0, scannedItem, "Bay Location", scannedItem, "York", "success");
            }

        } else if (val === workingLaneBarcode) {
            // Close this unload
            closeUnload();

            if (lastScanned === scannedItem) {
                logger(warehouse, "S1 Unloader", "Many", 0, scannedItem, "Lane Location", "York", scannedItem, "repeat");
            } else {
                logger(warehouse, "S1 Unloader", "Many", 0, scannedItem, "Lane Location", "York", scannedItem, "success");
            }
        } else {
            // Error
            logger(warehouse, "S1 Unloader", "Many", 0, scannedItem, "Bay Location", "N/A", "N/A", "wrong location");
            flashScanStatus("error");
            playError();
            toast.dismiss(); toast.error(t("wrong_location"));
        }

        set_lastScanned(scannedItem);

    }, [scan, workingLocation, workingLaneBarcode, lastScanned, warehouse, laneBarcodePrefix, navigator, t]);

    return (
        <div className="scan-panel">
            <ScanFlashOverlay active={scanStatus === "error"} />
            <div className="page-title-bar">
                <h1 className="page-title">{t("scan_location")}</h1>
                <button className="page-back-btn" onClick={resetFunc}><ArrowLeft size={20} /></button>
            </div>

            <div className="scan-centre">
                {/* Suppress the green success tick — only the error (red X) feedback shows here. */}
                {workingLocation === "" && <ScanStatusIcons status={scanStatus === "success" || scanStatus === "error" ? "idle" : scanStatus} />}

                {workingLocation === "" && (
                    <div className={`scan-command ${scanStatus !== "idle" ? "scan-command-hidden" : ""}`}>
                        {t("scan_location").toUpperCase().split(" ").map((word, i) => (
                            <FitWord key={i}>{word}</FitWord>
                        ))}
                    </div>
                )}
                {(workingLocation !== "" && workingLane != "L2") &&
                    <div className={`scan-feedback-area ${scanStatus === "error" ? "scan-command-hidden" : ""}`}>
                        <h1>{workingLocation}</h1>
                        <ArrowDownCircle />
                        <h1>{workingLane}</h1>
                    </div>
                }

                {(workingLocation !== "" && workingLane === "L2") && <ZoneStorageSorter1UnloadSingles warehouse={warehouse} workingLocation={workingLocation} set_workingLocation={set_workingLocation} closeUnload={closeUnload} flashScanStatus={flashScanStatus} scanValue={singlesScan} onScanChange={set_singlesScan} registerSubmit={registerSinglesSubmit} />}
            </div>

            {workingLane !== "L2" &&
                <HiddenScanInput value={scan} onChange={set_scan} onSubmit={doSubmit} disabled={false} disableCamera />
            }
            {workingLane === "L2" &&
                <HiddenScanInput value={singlesScan} onChange={set_singlesScan} onSubmit={handleSinglesSubmit} disabled={false} disableCamera />
            }
        </div>
    );
}

export default ZoneStorageSorter1Unload;
